import type { ReactNode } from "react";

import { Panel } from "../../design-system/surfaces.js";
import { TonePill, type StateTone } from "../../design-system/feed-state.js";
import { formatRelative } from "../../lib/format.js";
import { useNow } from "../../lib/use-now.js";
import type { FeedHealth, HealthMeasurement } from "../../api/types.js";

/**
 * What the product established about this feed, and how it established it.
 *
 * The verdict is one word; underneath it is every condition that failed, naming the
 * delivery it was found on, and underneath that is the evidence — what was actually
 * measured on each delivery, whether or not anything about it failed. The operator is
 * never asked to take a verdict on trust, which is the whole point of the product
 * watching its own outputs rather than telling him it is fine.
 */
const VERDICT: Record<FeedHealth["state"], { tone: StateTone; label: string }> =
  {
    healthy: { tone: "ok", label: "Healthy" },
    degraded: { tone: "warn", label: "Degraded" },
    down: { tone: "err", label: "Down" },
    unknown: { tone: "idle", label: "Not established" },
  };

export function HealthPanel({
  health,
  loading,
}: {
  health: FeedHealth | null;
  loading: boolean;
}) {
  const now = useNow(15_000);

  if (!health) {
    return (
      <Panel className="p-5">
        <Heading>Health</Heading>
        <p className="mt-3 text-sm text-ink-3">
          {loading ? "Loading status…" : "No status yet."}
        </p>
      </Panel>
    );
  }

  const verdict = VERDICT[health.state];

  return (
    <Panel className="p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Heading>Health</Heading>
        <span className="font-mono text-[0.6875rem] text-ink-3">
          {health.checkedAt
            ? `checked ${formatRelative(health.checkedAt, now)}`
            : "never checked"}
        </span>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-3">
        <TonePill label={verdict.label} pulse tone={verdict.tone} />
        {health.consecutiveFailures > 0 ? (
          <span className="font-mono text-[0.6875rem] text-ink-3">
            {`${String(health.consecutiveFailures)} consecutive failing checks`}
          </span>
        ) : null}
      </div>

      {health.detail ? (
        <p className="mt-2 text-sm leading-6 text-ink-2">{health.detail}</p>
      ) : null}

      {!health.publishes ? (
        <p className="mt-2 text-sm leading-6 text-ink-3">
          Add a delivery so listeners can tune in.
        </p>
      ) : null}

      {health.conditions.length > 0 ? (
        <ul className="mt-4 flex flex-col gap-2">
          {health.conditions.map((condition, index) => (
            <li
              key={`${condition.code}-${String(index)}`}
              className="rounded-sm border border-[color-mix(in_oklch,var(--warn)_28%,var(--border))] bg-[color-mix(in_oklch,var(--warn)_7%,transparent)] px-3 py-2"
            >
              <div className="font-mono text-[0.6875rem] tracking-[0.04em] text-warn">
                {condition.code}
              </div>
              <div className="mt-1 text-xs leading-5 text-ink-2">
                {condition.detail}
              </div>
              {condition.publishedUrl ? (
                <div className="mt-1 font-mono text-[0.625rem] break-all text-ink-3">
                  {condition.publishedUrl}
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <div className="mt-5 border-t border-border pt-4">
        <div className="font-mono text-[0.625rem] tracking-[0.12em] text-ink-3 uppercase">
          Evidence
        </div>
        {health.measurements.length === 0 ? (
          <p className="mt-2 text-xs text-ink-3">
            Nothing has been measured on this feed yet.
          </p>
        ) : (
          <div className="mt-3 flex flex-col gap-4">
            {health.measurements.map((measurement) => (
              <Measurement
                declared={health.declaredLoudness}
                key={measurement.deliveryId}
                measurement={measurement}
              />
            ))}
          </div>
        )}
      </div>

      <dl className="mt-5 grid grid-cols-2 gap-x-6 gap-y-2 border-t border-border pt-4 text-xs">
        <Fact label="Since">
          {health.stateSince ? formatRelative(health.stateSince, now) : "—"}
        </Fact>
        <Fact label="Last healthy">
          {health.lastHealthyAt
            ? formatRelative(health.lastHealthyAt, now)
            : "never"}
        </Fact>
        {health.fallbackSince ? (
          <Fact label="Floor on air since">
            {formatRelative(health.fallbackSince, now)}
          </Fact>
        ) : null}
        <Fact label="Checked every">
          {`${String(health.policy.checkIntervalSeconds)} s`}
        </Fact>
      </dl>
    </Panel>
  );
}

function Measurement({
  declared,
  measurement,
}: {
  declared: { integratedLufs: number; truePeakDbtp: number };
  measurement: HealthMeasurement;
}) {
  return (
    <div className="rounded-sm border border-border bg-bg-2 px-3 py-2.5">
      <div className="font-mono text-[0.625rem] break-all text-ink-2">
        {measurement.publishedUrl}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs sm:grid-cols-3">
        <Fact label="Reached">
          {measurement.reachable
            ? `yes${measurement.httpStatus ? ` · ${String(measurement.httpStatus)}` : ""}`
            : "no"}
        </Fact>
        <Fact label="Sampled">
          {measurement.sampledSeconds === null
            ? "—"
            : `${measurement.sampledSeconds.toFixed(1)} s`}
        </Fact>
        <Fact label="Bytes">{measurement.bytes.toLocaleString()}</Fact>
        <Fact label="Format">
          {measurement.codec
            ? [
                measurement.codec.toUpperCase(),
                measurement.bitrateKbps
                  ? `${String(measurement.bitrateKbps)} kbps`
                  : null,
                measurement.sampleRateHz
                  ? `${String(measurement.sampleRateHz)} Hz`
                  : null,
                measurement.channels === 1 ? "mono" : "stereo",
              ]
                .filter((part) => part !== null)
                .join(" · ")
            : "—"}
        </Fact>
        <Fact label="Integrated">
          {measurement.integratedLufs === null
            ? "—"
            : `${measurement.integratedLufs.toFixed(1)} LUFS (declared ${declared.integratedLufs.toFixed(1)})`}
        </Fact>
        <Fact label="True peak">
          {measurement.truePeakDbtp === null
            ? "—"
            : `${measurement.truePeakDbtp.toFixed(1)} dBTP (ceiling ${declared.truePeakDbtp.toFixed(1)})`}
        </Fact>
        <Fact label="Longest silence">
          {measurement.longestSilenceSeconds === null
            ? "—"
            : `${measurement.longestSilenceSeconds.toFixed(1)} s`}
        </Fact>
        <Fact label="Floor on air">
          {measurement.fallbackOnAir ? "yes" : "no"}
        </Fact>
        {measurement.streamTitle ? (
          <Fact label="On the wire">{measurement.streamTitle}</Fact>
        ) : null}
      </dl>
      {measurement.detail ? (
        <p className="mt-2 text-xs leading-5 text-ink-3">
          {measurement.detail}
        </p>
      ) : null}
    </div>
  );
}

function Heading({ children }: { children: ReactNode }) {
  return <h2 className="text-sm font-semibold text-ink">{children}</h2>;
}

function Fact({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="min-w-0">
      <dt className="font-mono text-[0.5625rem] tracking-[0.12em] text-ink-3 uppercase">
        {label}
      </dt>
      <dd className="mt-0.5 min-w-0 truncate text-ink-2">{children}</dd>
    </div>
  );
}
